import React from 'react';
import { Box, Container, Divider, Link, Typography } from '@mui/material';

const EFFECTIVE_DATE = 'May 2, 2026';
const CONTACT_EMAIL = 'privacy@utilibill.com';

export default function Privacy() {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography
        variant="h4"
        sx={{ fontFamily: "'Fraunces', serif", fontWeight: 700, color: 'text.primary', mb: 0.5 }}
      >
        Privacy Policy
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Effective {EFFECTIVE_DATE}
      </Typography>

      <Section title="1. Introduction">
        Utilibill (“we”, “us”, or “our”) provides utility billing software for property owners
        and their tenants. This Privacy Policy describes how we collect, use, and share
        personal information when you use our service.
      </Section>

      <Section title="2. Information We Collect">
        <ul>
          <li><strong>Account information:</strong> name, email address, and Firebase Authentication credentials.</li>
          <li><strong>Property and tenancy information:</strong> property address, unit assignment, billing share basis.</li>
          <li><strong>Payment information:</strong> bank account details collected and stored by our payment processor, Stripe, Inc. We do not store bank account numbers directly.</li>
          <li><strong>Usage data:</strong> invoice history, payment activity, and utility consumption data.</li>
        </ul>
      </Section>

      <Section title="3. How We Use Your Information">
        We use the information we collect to:
        <ul>
          <li>Calculate and send utility invoices to tenants;</li>
          <li>Process ACH payments via Stripe on behalf of property owners;</li>
          <li>Send transactional emails (invite, payment confirmation);</li>
          <li>Comply with applicable law, including Nacha Operating Rules and California law.</li>
        </ul>
        We do not sell personal information to third parties.
      </Section>

      <Section title="4. Payment Processing">
        ACH payment processing is provided by{' '}
        <Link href="https://stripe.com" target="_blank" rel="noopener noreferrer">
          Stripe, Inc.
        </Link>
        . When you connect a bank account, your information is subject to Stripe's{' '}
        <Link href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </Link>{' '}
        and{' '}
        <Link href="https://stripe.com/legal/ssa" target="_blank" rel="noopener noreferrer">
          Services Agreement
        </Link>
        . We store an ACH authorization timestamp for each user to satisfy the two-year
        audit-retention requirement under Nacha Operating Rules.
      </Section>

      <Section title="5. California Privacy Rights (CCPA)">
        If you are a California resident, you have the following rights under the California
        Consumer Privacy Act (CCPA):
        <ul>
          <li><strong>Right to Know:</strong> You may request details about the personal information we have collected about you and how we use it.</li>
          <li><strong>Right to Delete:</strong> You may request deletion of personal information we have collected, subject to legal retention obligations (e.g., Nacha two-year ACH authorization records).</li>
          <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your CCPA rights.</li>
          <li><strong>Right to Opt-Out of Sale:</strong> We do not sell personal information.</li>
        </ul>
        To submit a request, contact us at{' '}
        <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.
        We will respond within 45 days.
      </Section>

      <Section title="6. Utility Billing Disclosure (California)">
        If you are billed under a Ratio Utility Billing System (RUBS), your share of the
        property's utility bill is calculated using the method disclosed in your invitation
        email and displayed on each invoice. The calculation method is either an equal split
        among all units or a proportional share based on your unit's designated fraction of
        the total bill. You may request a recalculation explanation by contacting your
        property manager.
      </Section>

      <Section title="7. Data Retention">
        We retain personal information for as long as your account is active or as required
        by law. ACH authorization records are retained for a minimum of two years per Nacha
        Operating Rules. You may request deletion of other personal data as described above.
      </Section>

      <Section title="8. Contact Us">
        For privacy questions or to exercise your rights, contact us at{' '}
        <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.
      </Section>
    </Container>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.8 }}>
        {children}
      </Typography>
    </Box>
  );
}
